import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { defaultData } from './defaultData';
import {
    loginApi,
    fetchMinisters,
    fetchHeroSlides,
    fetchNews,
    fetchQuickLinks,
    fetchHomeSections,
    fetchPhotos,
    incrementVisitorCount,
    fetchActiveEvents,
    fetchNavigation,
    fetchLatestDocuments,
    BASE_URL
} from './services/api';

import { ConfigContext, useConfig } from './ConfigContextCore';

export { useConfig };


export const ConfigProvider = ({ children }) => {
    const [siteData, setSiteData] = useState(defaultData);
    const [visitorCount, setVisitorCount] = useState('000000');
    // Add dynamic navigation state
    const [dynamicNav, setDynamicNav] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [token, setToken] = useState(localStorage.getItem('admin_token'));
    const [user, setUser] = useState(JSON.parse(localStorage.getItem('admin_user')));

    const [fontSize, setFontSize] = useState(100);
    const [theme, setTheme] = useState('light');
    const [language, setLanguage] = useState(localStorage.getItem('site_language') || 'English');

    useEffect(() => {
        localStorage.setItem('site_language', language);
    }, [language]);

    // Optimized Load Site Data: Phase-based loading (GIGW/STQC Compliance)
    const loadSiteData = useCallback(async () => {
        try {
            // PHASE 1: UI Critical Data (Fastest Path to Render Navigation/Hero)
            const [navigation, hero, newsTicker, stats] = await Promise.all([
                fetchNavigation().catch(() => null),
                fetchHeroSlides().catch(() => null),
                fetchNews().catch(() => null),
                incrementVisitorCount().catch(() => null)
            ]);

            // Set UI Critical states immediately
            if (navigation && Array.isArray(navigation) && navigation.length > 0) setDynamicNav(navigation);
            if (stats && stats.visits) setVisitorCount(stats.visits.toString().padStart(6, '0'));

            setSiteData(prev => ({
                ...prev,
                hero: { slides: Array.isArray(hero) && hero.length > 0 ? hero : prev.hero.slides },
                newsTicker: Array.isArray(newsTicker) && newsTicker.length > 0 ? newsTicker : prev.newsTicker
            }));

            // PHASE 2: Content Heavy Data (Loaded in parallel after UI is interactive)
            const [ministers, quickLinks, homeSections, photos, activeEvents, latestDocs] = await Promise.all([
                fetchMinisters().catch(() => null),
                fetchQuickLinks().catch(() => null),
                fetchHomeSections().catch(() => null),
                fetchPhotos().catch(() => null),
                fetchActiveEvents().catch(() => []),
                fetchLatestDocuments(20).catch(() => []) // Performance: Limit to 20 for home page
            ]);

            setSiteData(prev => ({
                ...prev,
                ministers: Array.isArray(ministers) && ministers.length > 0 ? ministers : prev.ministers,
                gallery: Array.isArray(photos) && photos.length > 0 ? photos : prev.gallery,
                quickLinks: Array.isArray(quickLinks) && quickLinks.length > 0 ? quickLinks : prev.quickLinks,
                welcome: Array.isArray(homeSections) ? homeSections.find(s => s.section_key === 'welcome') || prev.welcome : prev.welcome,
                activeEvents: Array.isArray(activeEvents) ? activeEvents : [],
                // Memoized filters inside set state callback
                whatsNew: Array.isArray(latestDocs) && latestDocs.length > 0 ? {
                    circulars: latestDocs.filter(d => d.category === 'circular' || (d.category === 'info-news' && d.department?.toUpperCase() === 'CIRCULARS')).map(d => ({ title: d.title, link: d.file_path.startsWith('http') ? d.file_path : `${BASE_URL}/api/documents/download/${d.id}` })),
                    notifications: latestDocs.filter(d => d.category === 'notification' || (d.category === 'info-news' && d.department?.toUpperCase() === 'NOTIFICATIONS')).map(d => ({ title: d.title, link: d.file_path.startsWith('http') ? d.file_path : `${BASE_URL}/api/documents/download/${d.id}` })),
                    meetings: latestDocs.filter(d => d.category === 'sez-uac' || (d.category === 'info-news' && d.department?.toUpperCase() === 'MEETINGS')).map(d => ({ title: d.title, link: d.file_path.startsWith('http') ? d.file_path : `${BASE_URL}/api/documents/download/${d.id}` })),
                    reimbursement: latestDocs.filter(d => d.category === 'cst-docs' || (d.category === 'info-news' && d.department?.toUpperCase() === 'REIMBURSEMENT')).map(d => ({ title: d.title, link: d.file_path.startsWith('http') ? d.file_path : `${BASE_URL}/api/documents/download/${d.id}` })),
                    others: latestDocs.filter(d => d.category === 'info-news' && !['CIRCULARS', 'NOTIFICATIONS', 'MEETINGS', 'REIMBURSEMENT'].includes(d.department?.toUpperCase())).map(d => ({ title: d.title, link: d.file_path.startsWith('http') ? d.file_path : `${BASE_URL}/api/documents/download/${d.id}` }))
                } : prev.whatsNew
            }));

            console.log('⚡ Performance Optimized: Site data loaded in phases');
        } catch (err) {
            console.error('Failed to load site data (Phase 2 Fallback active):', err);
        }
    }, []);

    useEffect(() => {
        // ACTIVATED DYNAMIC CONTENT (PHASE 2)
        loadSiteData();
    }, [loadSiteData]);

    useEffect(() => {
        if (token) {
            setIsAdmin(true);
        }
    }, [token]);

    useEffect(() => {
        document.documentElement.style.fontSize = `${fontSize}%`;
    }, [fontSize]);

    useEffect(() => {
        document.body.className = theme === 'dark' ? 'dark-theme' : '';
    }, [theme]);

    const changeFontSize = (type) => {
        if (type === 'increase') setFontSize(prev => Math.min(prev + 10, 130));
        else if (type === 'decrease') setFontSize(prev => Math.max(prev - 10, 80));
        else setFontSize(100);
    };

    const login = async (username, password) => {
        try {
            const data = await loginApi(username, password);
            setToken(data.token);
            setUser(data.user);
            setIsAdmin(true);
            localStorage.setItem('admin_token', data.token);
            localStorage.setItem('admin_user', JSON.stringify(data.user));
            return { success: true };
        } catch (err) {
            return { success: false, message: err.message };
        }
    };

    const logout = useCallback(() => {
        setIsAdmin(false);
        setToken(null);
        setUser(null);
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
    }, []);

    // 15-Minute Idle Timeout Layer (GIGW 4.2 Security Compliance)
    useEffect(() => {
        if (!isAdmin || !token) return;

        let idleTimer;
        let warningActive = false;

        const handleIdle = async () => {
            if (warningActive) return;
            warningActive = true;

            const { default: swalUtils } = await import('./utils/swal');
            const result = await swalUtils.confirm(
                'Security Alert',
                'Your session has been idle for 15 minutes. For your security, would you like to continue or logout?',
                'Continue Session',
                'Logout Now'
            );

            warningActive = false;
            if (result.isConfirmed) {
                resetIdleTimer();
            } else {
                logout();
            }
        };

        const resetIdleTimer = () => {
            clearTimeout(idleTimer);
            // 15 Minutes = 15 * 60 * 1000ms
            idleTimer = setTimeout(handleIdle, 15 * 60 * 1000);
        };

        // Interaction Listeners
        const events = ['mousemove', 'keydown', 'scroll', 'click', 'touchstart'];
        events.forEach(e => window.addEventListener(e, resetIdleTimer));

        resetIdleTimer(); // Initial boot

        return () => {
            clearTimeout(idleTimer);
            events.forEach(e => window.removeEventListener(e, resetIdleTimer));
        };
    }, [isAdmin, token, logout]);

    // Global Session Expiry Listener (Reactive to api.js 401 calls)
    useEffect(() => {
        const handleExpiry = async () => {
            logout();
            const { default: swalUtils } = await import('./utils/swal');
            swalUtils.warning(
                'Session Expired', 
                'Your administrative session has ended for security. Please login again to continue your work.'
            );
        };

        window.addEventListener('VSEZ_SESSION_EXPIRED', handleExpiry);
        return () => window.removeEventListener('VSEZ_SESSION_EXPIRED', handleExpiry);
    }, [logout]);

    return (
        <ConfigContext.Provider value={{
            siteData,
            visitorCount,
            isAdmin,
            token,
            user,
            fontSize,
            language,
            theme,
            dynamicNav,
            setDynamicNav,
            login,
            logout,
            changeFontSize,
            setFontSize,
            setLanguage,
            setTheme,
            loadSiteData
        }}>
            {children}
        </ConfigContext.Provider>
    );
};
